import { createRecordType, defineMigrations } from "@supersig/store";
import { T } from "@supersig/validate";
import { nanoid } from "nanoid";
import { localizationProviderIdValidator } from "../localizationProviders";
import { idValidator } from "../misc/id-validator";
export const PresentationStyles = [
    "FULLSCREEN",
    "MODAL",
    "PUSH",
    "NO_ANIMATION",
    "DRAWER"
];
export const PresentationConditions = [
    "CHECK_USER_SUBSCRIPTION",
    "ALWAYS"
];
export const FeatureGatingValues = [
    "GATED",
    "NON_GATED"
];
export const ReRouteBackButtonValues = [
    "ENABLED",
    "DISABLED"
];
export const IsScrollEnabledValues = [
    "ENABLED",
    "DISABLED"
];
export const GameControllerSupportValues = [
    "ENABLED",
    "DISABLED"
];
export const OnDeviceCacheValues = [
    "ENABLED",
    "DISABLED"
];
export const TemplateTypeValues = [
    "GLOBAL",
    "UNLISTED"
]// Can in theory be "ORGANIZATION" as well
;
export const WebCheckoutDestination = [
    "EXTERNAL",
    "IN_APP_BROWSER",
    "PAYMENT_SHEET",
    "PAYWALL"
];
export const CheckoutDetailsVersionValues = [
    0,
    1,
    2
];
export const CheckoutKbdButtonVersionValues = [
    0,
    1,
    2,
    3,
    4
];
export const CheckoutFooterVersionValues = [
    0,
    1,
    2,
    3
];
export const LocalizationTypeValues = [
    "SIMPLE",
    "ADVANCED"
];
export const FormalityLevelValues = [
    "INFORMAL",
    "FORMAL"
];
export const IntroductoryOfferEligibilityValues = [
    "AUTOMATIC",
    "ALWAYS_ELIGIBLE",
    "ALWAYS_INELIGIBLE"
];
export const PaywallValidator = T.model("paywall", T.object({
    id: idValidator("paywall"),
    typeName: T.literal("paywall"),
    version: T.number,
    name: T.string.optional(),
    databaseId: T.string.optional(),
    identifier: T.string.optional(),
    presentationCondition: T.literalEnum(...PresentationConditions),
    presentationStyle: T.literalEnum(...PresentationStyles),
    reRouteBackButton: T.literalEnum(...ReRouteBackButtonValues).optional(),
    gameControllerSupport: T.literalEnum(...GameControllerSupportValues).optional(),
    featureGating: T.literalEnum(...FeatureGatingValues),
    onDeviceCache: T.literalEnum(...OnDeviceCacheValues),
    isScrollEnabled: T.literalEnum(...IsScrollEnabledValues).optional(),
    editable: T.boolean,
    webCheckoutDestination: T.literalEnum(...WebCheckoutDestination),
    introductoryOfferEligibility: T.literalEnum(...IntroductoryOfferEligibilityValues).optional(),
    templateType: T.literalEnum(...TemplateTypeValues).optional(),
    templateCategories: T.string.optional(),
    templateId: T.string.optional(),
    localizationProvider: T.object({
        id: localizationProviderIdValidator,
        internalLocalizationProviderId: T.number,
        projectDisplayName: T.string
    }).optional(),
    localizationType: T.literalEnum(...LocalizationTypeValues).optional(),
    simpleLocalizationSettings: T.object({
        formalityLevel: T.literalEnum(...FormalityLevelValues),
        styleGuide: T.string
    }).optional(),
    presentationStyleV3: T.union("type", {
        DRAWER: T.object({
            type: T.literal("DRAWER"),
            height: T.number,
            cornerRadius: T.number
        }),
        POPUP: T.object({
            type: T.literal("POPUP"),
            width: T.number,
            height: T.number,
            cornerRadius: T.number
        }),
        MODAL: T.object({
            type: T.literal("MODAL")
        }),
        FULLSCREEN: T.object({
            type: T.literal("FULLSCREEN")
        }),
        PUSH: T.object({
            type: T.literal("PUSH")
        }),
        NO_ANIMATION: T.object({
            type: T.literal("NO_ANIMATION")
        })
    }).optional(),
    paymentSheetConfig: T.object({
        forceCloseButton: T.boolean.optional(),
        detailsVersion: T.literalEnum(...CheckoutDetailsVersionValues).optional(),
        kbdButtonVersion: T.literalEnum(...CheckoutKbdButtonVersionValues).optional(),
        footerVersion: T.literalEnum(...CheckoutFooterVersionValues).optional()
    }).optional()
}));
const Versions = {
    AddWebCheckoutDestination: 1
};
export const SWPaywallRecordType = createRecordType("paywall", {
    validator: PaywallValidator,
    scope: "document",
    migrations: defineMigrations({
        currentVersion: Versions.AddWebCheckoutDestination,
        migrators: {
            [Versions.AddWebCheckoutDestination]: {
                up: ({ ...rest })=>{
                    return {
                        ...rest,
                        webCheckoutDestination: "EXTERNAL"
                    };
                },
                down: ({ webCheckoutDestination, ...rest })=>({
                        ...rest
                    })
            }
        }
    })
}).withDefaultProperties(()=>({
        name: "New Paywall - " + new Date().toLocaleDateString(),
        presentationCondition: "CHECK_USER_SUBSCRIPTION",
        presentationStyle: "FULLSCREEN",
        presentationStyleV3: {
            type: "FULLSCREEN"
        },
        reRouteBackButton: "DISABLED",
        gameControllerSupport: "DISABLED",
        featureGating: "NON_GATED",
        onDeviceCache: "ENABLED",
        isScrollEnabled: "ENABLED",
        webCheckoutDestination: "EXTERNAL"
    }));
export function isPaywall(record) {
    if (!record) return false;
    return record.typeName === "paywall";
}
export function isPaywallId(id) {
    if (!id) return false;
    return id.startsWith("paywall:");
}
/** @public */ export function createPaywallId(id) {
    return `paywall:${id ?? nanoid()}`;
}
export const paywallId = createPaywallId("paywall");
